import React, { useState, memo } from 'react';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import MuiTable from '@material-ui/core/Table';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableContainer from '@material-ui/core/TableContainer';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTablePagination from '@material-ui/core/TablePagination';
import { useMutation, useQuery } from 'react-query';
import MuiPaper from '@material-ui/core/Paper';
import * as API from 'api';
import LinkfireHistoryRow from './linkfire-history-row';
import MuiBox from '@material-ui/core/Box';
import MuiButton from '@material-ui/core/Button';
import MuiAutoRenew from '@mui/icons-material/Autorenew';
import MuiFileIcon from '@mui/icons-material/InsertDriveFile';
import { withStyles } from '@material-ui/core/styles';
import useTableSelection from 'hooks/useTableSelection';
import TableSelectColumn from 'components/TableSelectColumn';

const LinkfireIngestionHistory = API.LinkfireIngestionHistory();

const styles = theme => ({
  actionButton: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
});

function LinkfireHistoryTable({ ...props }) {
  const { classes } = props;
  const [params, setParams] = useState({ page: 0, rowsPerPage: 25 });
  const { data, isLoading, refetch } = useQuery(['linkfire-ingestion-history', params.page, params.rowsPerPage], () => {
    return LinkfireIngestionHistory.getFilesHistory({
      page_index: params.page,
      page_size: params.rowsPerPage,
    });
  });
  const runFilesAgain = useMutation(() => LinkfireIngestionHistory.runFileAgain(selectedIds), {
    onSuccess: () => {
      refetch();
      clearSelection();
    },
    onError: error => {
      alert(error.data?.message || 'Unknown Error');
      runFilesAgain.reset();
    },
  });
  const { selectedIds, isAllSelected, isSomeSelected, isSelected, toggleSelection, toggleSelectAll, clearSelection } =
    useTableSelection(data?.results || [], 'file_name');

  function handleChangePage(_, page) {
    setParams({ ...params, page: page });
  }

  function handleChangeRowsPerPage({ target: { value } }) {
    setParams({ ...params, page: 0, rowsPerPage: value });
  }

  function handleRunAllAgain() {
    runFilesAgain.mutate();
  }

  if (isLoading) return <MuiCircularProgress />;

  return (
    <>
      <MuiBox display="flex" flexDirection="row" justifyContent="flex-start" alignItems="baseline">
        <MuiButton
          variant="contained"
          size="small"
          className={classes.actionButton}
          startIcon={<MuiAutoRenew />}
          onClick={refetch}>
          Refresh
        </MuiButton>
        {runFilesAgain.isLoading ? (
          <MuiCircularProgress size={20} color="primary" />
        ) : (
          <MuiButton
            variant="contained"
            size="small"
            className={classes.actionButton}
            startIcon={<MuiFileIcon />}
            onClick={handleRunAllAgain}
            disabled={selectedIds.length === 0}>
            Run all again
          </MuiButton>
        )}
      </MuiBox>
      <MuiTableContainer component={MuiPaper}>
        <MuiTable aria-label="collapsible table">
          <MuiTableHead>
            <MuiTableRow>
              <TableSelectColumn
                checked={isAllSelected}
                indeterminate={isSomeSelected}
                onChange={toggleSelectAll}
                tooltip="Select all files"
              />
              <MuiTableCell>File name</MuiTableCell>
              <MuiTableCell>Status</MuiTableCell>
              <MuiTableCell>Created at</MuiTableCell>
              <MuiTableCell>Records imported</MuiTableCell>
              <MuiTableCell></MuiTableCell>
            </MuiTableRow>
          </MuiTableHead>
          <MuiTableBody>
            <MuiTableRow>
              <MuiTablePagination
                rowsPerPageOptions={[25, 50, 100]}
                rowsPerPage={params.rowsPerPage}
                count={data?.total || 0}
                page={params.page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </MuiTableRow>
            {data?.results?.map(file_history => (
              <LinkfireHistoryRow
                key={file_history.file_name}
                {...file_history}
                refetch={refetch}
                isSelected={isSelected(file_history.file_name)}
                onToggleSelect={() => toggleSelection(file_history.file_name)}
                isRunningFilesAgain={runFilesAgain.isLoading}
              />
            ))}
            <MuiTableRow>
              <MuiTablePagination
                rowsPerPageOptions={[25, 50, 100]}
                rowsPerPage={params.rowsPerPage}
                count={data?.total || 0}
                page={params.page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </MuiTableRow>
          </MuiTableBody>
        </MuiTable>
      </MuiTableContainer>
    </>
  );
}

export default withStyles(styles)(memo(LinkfireHistoryTable));
